import {Injectable, Signal, WritableSignal, inject, signal} from '@angular/core';
import {getMessaging, getToken, Messaging, onMessage} from "@angular/fire/messaging";
import {environment} from "../../../environments/environment";
import {ErrorService} from "./error.service";
import {LanguageService} from "./language.service";
import {initializeApp} from '@angular/fire/app';
import {ToasterService} from './toaster.service';
import {ApiService} from './api.service';
import {TokenService} from './token.service';
import {take} from 'rxjs';
import {TYPE} from '../types/types';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  private messaging: Messaging = inject(Messaging);
  private errorService: ErrorService = inject(ErrorService);
  private languageService: LanguageService = inject(LanguageService);
  private toaster: ToasterService = inject(ToasterService);
  private apiService: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);

  private token: string | null = null;
  private isInitialized: boolean = false;
  private isSafari: Signal<boolean> = signal(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));

  public notificationState: WritableSignal<TYPE.FirebaseNotificationsState> = signal<TYPE.FirebaseNotificationsState>(TYPE.FirebaseNotificationsState.INITIALIZING);

  public getToken(): string | null {
    return this.token;
  }

  public setToken(token: string | null): void {
    this.token = token;
    if (token && this.tokenService.hasToken()) {
      this.apiService.setUserFirebase({
        device_token: token,
        device_type: 'web'
      }).pipe(take(1)).subscribe(() => { /* empty */ });
    }
  }

  public resetState(): void {
    this.token = null;
    this.notificationState.set(TYPE.FirebaseNotificationsState.INITIALIZING);
  }

  public init(asUserAction: boolean = false): void {
    if (!environment.firebase.active) {
      return;
    }
    if (this.isInitialized) {
      if (asUserAction && Notification.permission === 'default') {
        this.checkNotificationPermission(true);
      }
      return;
    }
    this.isInitialized = true;
    let permissionRequest: Promise<NotificationPermission> | null = null;
    if (asUserAction && Notification.permission === 'default') {
      // Firefox requires starting this directly in the user click handler call stack.
      permissionRequest = Notification.requestPermission();
    }
    const app = initializeApp(environment.firebase.config);
    this.messaging = getMessaging(app);
    this.setupServiceWorker(asUserAction, permissionRequest);
  }

  private async setupServiceWorker(
    asUserAction: boolean = false,
    permissionRequest: Promise<NotificationPermission> | null = null
  ): Promise<void> {
    this.log('⚙ Setting up service worker...');
    
    try {
      if ('serviceWorker' in navigator) {
        this.log('✅ Service worker supported');
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {scope: '/'});
        this.log('✅ Service worker registered successfully');
        if(registration.active) {
          this.log('✅ Service worker state: ' + registration.active?.state);
          this.checkNotificationPermission(asUserAction, permissionRequest);
        } else {
          this.log('⚠️ Service worker state: inactive');
          registration.addEventListener('updatefound', () => {
            const installingWorker = registration.installing;
            if(installingWorker) {
              this.log('⚙ Installing worker found, setting up messaging service worker...');
              installingWorker.addEventListener('statechange', () => {
                if(installingWorker.state === 'installed') {
                  this.log('✅ Installing worker state: installed');
                  this.checkNotificationPermission(asUserAction, permissionRequest);
                } else {
                  this.log('⚙ Installing worker state: ' + installingWorker.state);
                }
              });
            } else {
              this.notificationState.set(TYPE.FirebaseNotificationsState.ERROR);
              this.log('❌ No installing worker found, messaging service worker not set up.')
            }
          });
        }
      } else {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNSUPPORTED);
        console.error('❌ Service worker not supported');
      }
    } catch (error) {
      this.notificationState.set(TYPE.FirebaseNotificationsState.ERROR);
      console.error('❌ Error setting up service worker:', error);
    }
  }

  private async checkNotificationPermission(
    asUserAction: boolean = false,
    permissionRequest: Promise<NotificationPermission> | null = null
  ): Promise<void> {
    let permission = Notification.permission;
    if (permissionRequest) {
      permission = await permissionRequest;
    } else if (permission === 'default' && asUserAction) {
      permission = await Notification.requestPermission();
    }
    if(permission === 'granted') {
      this.log('✅ Notification permission granted');
      this.notificationState.set(TYPE.FirebaseNotificationsState.GRANTED);
      this.setupFirebaseMessaging();
    } else if (permission === 'denied') {
      this.log('❌ Notification permission denied');
      this.notificationState.set(TYPE.FirebaseNotificationsState.DENIED);
    } else {
      this.log('? Notification permission unhandled, permission state: ' + permission);
      if(permission === 'default') {
        this.notificationState.set(TYPE.FirebaseNotificationsState.DEFAULT);
      } else {
        this.notificationState.set(TYPE.FirebaseNotificationsState.UNDEFINED);
      }
    }
  }

  private async setupFirebaseMessaging(): Promise<void> {
    this.log('⚙ Setting up Firebase Messaging...');
    
    try {
      const firebaseToken = await getToken(
        this.messaging as Messaging, 
        {
          vapidKey: environment.firebase.vapidKey,
          serviceWorkerRegistration: await navigator.serviceWorker.getRegistration()
        }
      );
      if(firebaseToken) {
        this.log('✅ Firebase token received: ' + firebaseToken);
        this.setToken(firebaseToken);
        this.log('⚙ Setting up Firebase Messaging listener...');
        onMessage(this.messaging, (payload) => {
          this.log("📩 Firebase Message - Received " + payload);
          this.toaster.info(payload.notification?.title ?? '', payload.notification?.body ?? '');
        });
      } else {
        this.log('❌ No Firebase token received');
      }
    } catch (error) {
      console.error('❌ Error setting up Firebase Messaging: ' + error);
      this.errorService.setError(this.languageService.getTranslationByKey('ERROR'), error, false, false);
    }
  }

  private log(message: string): void {
    if(environment.firebase.setupLogs) {
      console.log('🔥 ' + message);
    }
  }
}
